import type { Room } from "colyseus.js";
import { createRoom, joinRoom, reconnectRoom } from "./net";
import {
  GAMES, CATEGORIES, NAME_MAX, APP_NAME, BOT_LEVELS, LEADERBOARD_IDS, PLAY_MODES,
  type GameMeta, type GameCategory, type PlayMode,
} from "../shared/protocol";
import { makeGameClient, WEB_GAME_IDS, type ClientGame } from "./games";
import { loadBubble } from "./bubbleSave";
import { playerId, playerName, setPlayerName } from "./identity";
import { sound } from "./sound";
import { wakeLock } from "./wakelock";
import { qrSvg } from "./qr";
import { gameIcon, gameTint } from "./gameIcons";
import { fmtScore, fetchBest } from "./bestScore";
import { getFeatured, submitFeedback, getLeaderboard, type FeedbackType } from "./api";
import { HOW_TO_PLAY } from "./howToPlay";
import { soundEnabled, setSoundEnabled } from "./settings";
import { appStoreBadge, playStoreBadge, APP_STORE_URL, PLAY_STORE_URL } from "./storeBadges";

const app = document.getElementById("app")!;

let room: Room | null = null;
let mySlot = -1;
let currentView = "";
let game: ClientGame | null = null;
let homeError = "";
let lobbyCat: GameCategory | "all" = "all";
let lobbySearch = "";
let endSoundPlayed = false;
let qrOpen = false;
let gameLayout: "grid" | "list" =
  (localStorage.getItem("su_gamelayout") as any) === "list" ? "list" : "grid";
let deliberateLeave = false;
let reconnecting = false;
let lastToken = "";
let featuredIds: string[] = [];
let lbBrowse: { game: string; top: BoardEntry[] } | null = null;

void getFeatured().then((ids) => { featuredIds = ids; if (currentView === "lobby") renderLobby(); });

// solo leaderboard
type SoloResult = { game: string; score: number; detail: string };
type BoardEntry = { name: string; score: number; detail: string; tag: string; mine: boolean };
let soloResult: SoloResult | null = null;
let board: { game: string; top: BoardEntry[]; rank?: number; best?: boolean } | null = null;
let boardBusy = false;
let boardOpen = false;
let lbLobbyOpen = false;

const savedName = () => playerName();
const rememberName = (n: string) => setPlayerName(n);

window.addEventListener("pointerdown", () => sound.unlock(), { once: true });

// coming back from a backgrounded tab: if our socket died while frozen, recover now
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible" || !room || reconnecting || deliberateLeave || !lastToken) return;
  const ws = (room as any)?.connection?.transport?.ws;
  if (ws && ws.readyState !== 1) void tryReconnect();
});

(window as any).__pa = {
  get room() { return room; },
  get game() { return game; },
  get mySlot() { return mySlot; },
};

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<HTMLElementTagNameMap[K]> & { class?: string } = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] => {
  const n = document.createElement(tag);
  const { class: cls, ...rest } = props as any;
  if (cls) n.className = cls;
  Object.assign(n, rest);
  for (const c of children) n.append(c as any);
  return n;
};

const svg = (markup: string) => {
  const span = document.createElement("span");
  span.innerHTML = markup;
  return span.firstElementChild as SVGElement;
};

function adSlot() {
  return el("div", { class: "adslot" }, ["advertisement"]);
}

function playersBySlot(): (any | null)[] {
  const out: (any | null)[] = [null, null];
  room!.state.players.forEach((p: any) => {
    if (p.slot === 0 || p.slot === 1) out[p.slot] = p;
  });
  return out;
}

const gameMeta = (id: string) => GAMES.find((g) => g.id === id);
const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

// ---------------- HOME ----------------

function renderHome() {
  currentView = "home";
  const params = new URLSearchParams(location.search);
  const prefillCode = (params.get("code") || "").toUpperCase();

  const nameInput = el("input", {
    type: "text", value: savedName(), maxLength: NAME_MAX, placeholder: "your name",
  });
  const codeInput = el("input", {
    type: "text", class: "code-input", value: prefillCode, maxLength: 4,
    placeholder: "CODE", autocapitalize: "characters",
  });
  const err = el("div", { class: "error" }, [homeError]);
  const name = () => (nameInput.value.trim() || "Player").slice(0, NAME_MAX);

  const start = async (btn: HTMLButtonElement, opts: { mode: string; vsBot?: boolean; botLevel?: string }) => {
    homeError = ""; rememberName(nameInput.value.trim()); btn.disabled = true;
    try { attach(await createRoom(name(), opts)); }
    catch { homeError = "Could not start."; btn.disabled = false; render(); }
  };

  const soloBtn = modeTile("mango", "Just me", "beat your best", GLYPH_ONE);
  soloBtn.onclick = () => start(soloBtn, { mode: "solo" });

  const cpuBtn = modeTile("coral", "Me vs Dot", "play the computer", GLYPH_DOT);
  cpuBtn.onclick = () => start(cpuBtn, { mode: "cpu", vsBot: true, botLevel: "medium" });

  const friendBtn = modeTile("cobalt", "Two of us", "on two phones", GLYPH_PHONES);
  friendBtn.onclick = () => start(friendBtn, { mode: "1v1" });

  const joinBtn = el("button", {}, ["Join"]);
  joinBtn.onclick = async () => {
    const code = codeInput.value.trim().toUpperCase();
    if (code.length !== 4) { homeError = "Enter the 4-letter code."; return render(); }
    homeError = ""; rememberName(nameInput.value.trim()); joinBtn.disabled = true;
    try { attach(await joinRoom(code, name())); }
    catch (e: any) { homeError = e?.message || "Could not join."; joinBtn.disabled = false; render(); }
  };

  const menuBtn = el("button", { class: "menu-btn", title: "Menu" }, [
    svg(`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M5 7h14M5 12h14M5 17h14"/></svg>`),
  ]);
  menuBtn.setAttribute("aria-label", "Menu");
  menuBtn.onclick = () => renderMenu();

  const lbBtn = el("button", { class: "menu-btn trophy-btn", title: "Leaderboards" }, [
    svg(`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 4h10v5a5 5 0 0 1-10 0z"/><path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3M12 14v3M8 20h8M9.5 17h5"/></svg>`),
  ]);
  lbBtn.setAttribute("aria-label", "Leaderboards");
  lbBtn.onclick = () => renderLeaderboards();

  nameInput.setAttribute("aria-label", "Your name, shown on leaderboards");
  codeInput.setAttribute("aria-label", "Room code");

  app.replaceChildren(
    el("div", { class: "screen", id: "screen-home" }, [
      el("div", { class: "home-top" }, [
        el("h1", { class: "logo home-logo" }, [logoTitle()]),
        el("div", { class: "top-btns" }, [lbBtn, menuBtn]),
      ]),
      el("div", { class: "mode-tiles" }, [soloBtn, cpuBtn, friendBtn]),
      el("div", { class: "home-extras" }, [
        el("div", { class: "home-field" }, [el("span", { class: "hf-label" }, ["Your name"]), nameInput]),
        el("div", { class: "home-field" }, [el("span", { class: "hf-label" }, ["Got a code?"]), codeInput, joinBtn]),
        err,
      ]),
      adSlot(),
    ]),
  );
}

// home mode tiles: a big solid-colour sticker, label left, a dot glyph right
// (same picture language as the native app — one dot is you, Dot is the CPU)
const GLYPH_ONE = `<svg viewBox="0 0 76 76" aria-hidden="true"><circle cx="38" cy="38" r="22" fill="#FFFFFF" stroke="#1B1534" stroke-width="4"/></svg>`;
const GLYPH_DOT = `<svg viewBox="0 0 76 76" aria-hidden="true"><circle cx="22" cy="38" r="15" fill="#FFFFFF" stroke="#1B1534" stroke-width="4"/><circle cx="54" cy="38" r="15" fill="#37D67A" stroke="#1B1534" stroke-width="4"/></svg>`;
const GLYPH_PHONES = `<svg viewBox="0 0 76 76" aria-hidden="true"><rect x="3" y="12" width="30" height="52" rx="8" fill="#FFFFFF" stroke="#1B1534" stroke-width="4"/><circle cx="18" cy="38" r="8" fill="#1B1534"/><rect x="43" y="12" width="30" height="52" rx="8" fill="#FFFFFF" stroke="#1B1534" stroke-width="4"/><circle cx="58" cy="38" r="8" fill="#1B1534"/></svg>`;

function modeTile(colour: string, title: string, sub: string, glyph: string): HTMLButtonElement {
  const b = el("button", { class: `mode-tile ${colour}` }, [
    el("span", {}, [
      el("span", { class: "mt-title" }, [title]),
      el("span", { class: "mt-sub" }, [sub]),
    ]),
    svg(glyph),
  ]);
  b.setAttribute("aria-label", `${title}, ${sub}`);
  return b;
}

const BACK_SVG = `<svg viewBox="0 0 12 18" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 2 L3 9 L9 16"/></svg>`;
function backButton(): HTMLButtonElement {
  const b = el("button", { class: "link-back", title: "Back" }, [svg(BACK_SVG)]);
  b.setAttribute("aria-label", "Back");
  return b;
}

// ---------------- MENU (settings + legal + apps) ----------------

function renderMenu() {
  currentView = "settings";

  const backBtn = backButton();
  backBtn.onclick = () => renderHome();

  const soundToggle = el("button", {
    class: `toggle ${soundEnabled() ? "on" : ""}`,
    role: "switch",
  }, [el("span", { class: "knob" }, [])]);
  soundToggle.setAttribute("aria-checked", String(soundEnabled()));
  soundToggle.onclick = () => {
    const next = !soundEnabled();
    setSoundEnabled(next);
    soundToggle.classList.toggle("on", next);
    soundToggle.setAttribute("aria-checked", String(next));
    if (next) sound.unlock();
  };

  const bugBtn = el("button", { class: "row-btn" }, [
    el("span", {}, ["Report a bug"]), el("span", { class: "chev" }, ["›"]),
  ]);
  bugBtn.onclick = () => openFeedback("bug");
  const suggestBtn = el("button", { class: "row-btn" }, [
    el("span", {}, ["Suggest a game"]), el("span", { class: "chev" }, ["›"]),
  ]);
  suggestBtn.onclick = () => openFeedback("suggestion");

  const storeLink = (href: string, badge: string, label: string) => {
    const a = el("a", { href, target: "_blank", rel: "noopener", ariaLabel: label });
    a.appendChild(svg(badge));
    return a;
  };
  const appsBadges = el("div", { class: "apps-badges" }, [
    storeLink(APP_STORE_URL, appStoreBadge, "Download on the App Store"),
    storeLink(PLAY_STORE_URL, playStoreBadge, "Get it on Google Play"),
  ]);

  const legalLink = (href: string, label: string) => {
    const a = el("a", { class: "row-btn", href, target: "_blank", rel: "noopener" }, [
      el("span", {}, [label]), el("span", { class: "chev" }, ["›"]),
    ]);
    return a;
  };

  app.replaceChildren(
    el("div", { class: "screen", id: "screen-settings" }, [
      el("div", { class: "settings-head" }, [backBtn, el("div", { class: "brand" }, ["Menu"]), el("span", {})]),
      el("div", { class: "card settings-card" }, [
        el("div", { class: "settings-sec" }, ["Preferences"]),
        el("div", { class: "settings-row" }, [
          el("div", { class: "settings-row-text" }, [
            el("div", { class: "srl" }, ["Sound effects"]),
            el("div", { class: "srs" }, ["Games with audio honour this"]),
          ]),
          soundToggle,
        ]),
      ]),
      el("div", { class: "card settings-card" }, [
        el("div", { class: "settings-sec" }, ["Feedback"]),
        bugBtn,
        el("div", { class: "settings-divider" }, []),
        suggestBtn,
      ]),
      el("div", { class: "card settings-card apps-card" }, [
        el("div", { class: "settings-sec" }, ["Mobile apps"]),
        appsBadges,
        el("div", { class: "srs" }, ["Get the free JACE.GAMES app for iPhone and Android — every game, made for your phone."]),
      ]),
      el("div", { class: "card settings-card" }, [
        el("div", { class: "settings-sec" }, ["Legal"]),
        legalLink("/privacy", "Privacy Policy"),
        el("div", { class: "settings-divider" }, []),
        legalLink("/terms", "Terms of Use"),
      ]),
      el("div", { class: "card settings-card" }, [
        el("div", { class: "settings-sec" }, ["About"]),
        el("div", { class: "settings-about" }, [
          `${APP_NAME} is a room-based 1v1 + solo game platform. One player makes a room, a friend joins with the 4-letter code, and you swap games without leaving the room.`,
        ]),
      ]),
    ]),
  );
}

// ---------------- LEADERBOARDS (standalone browser) ----------------

function renderLeaderboards(game?: string) {
  currentView = "leaderboards";
  const games = GAMES.filter((g) => g.leaderboard);
  const sel = game || lbBrowse?.game || games[0]?.id || "";

  if (sel && lbBrowse?.game !== sel) {
    getLeaderboard(sel).then((top) => {
      lbBrowse = { game: sel, top };
      if (currentView === "leaderboards") renderLeaderboards(sel);
    });
  }
  const fresh = lbBrowse?.game === sel;
  const meta = gameMeta(sel);

  const backBtn = backButton();
  backBtn.onclick = () => renderHome();

  const tabs = el("div", { class: "lb-tabs" },
    games.map((g) => {
      const b = el("button", { class: `lb-tab ${g.id === sel ? "on" : ""}` }, [
        el("span", { class: "lb-tab-ico" }, [g.icon]), g.name,
      ]);
      b.onclick = () => renderLeaderboards(g.id);
      return b;
    }),
  );

  const list = fresh
    ? renderBoardList(lbBrowse!.top)
    : el("div", { class: "hint centered" }, ["Loading…"]);

  app.replaceChildren(
    el("div", { class: "screen", id: "screen-leaderboards" }, [
      el("div", { class: "settings-head" }, [backBtn, el("div", { class: "brand" }, ["Leaderboards"]), el("span", {})]),
      tabs,
      el("div", { class: "card" }, [
        el("div", { class: "lb-head" }, [
          el("label", {}, [meta?.name ?? ""]),
          ...(meta?.unit ? [el("span", { class: "lb-unit" }, [`by ${meta.unit}`])] : []),
        ]),
        list,
      ]),
      adSlot(),
    ]),
  );
}

// ---------------- FEEDBACK MODAL ----------------

function openFeedback(type: FeedbackType) {
  const existing = document.querySelector(".fb-overlay");
  if (existing) existing.remove();

  const isBug = type === "bug";
  const ta = el("textarea", {
    class: "fb-text",
    placeholder: isBug ? "Which game, and what went wrong?" : "What game should we add?",
    rows: 5,
  });
  const errLine = el("div", { class: "fb-err", hidden: true }, ["Couldn't send — check your connection."]);
  const sendBtn = el("button", { class: "primary" }, ["Send"]);
  const cancelBtn = el("button", { class: "ghost" }, ["Cancel"]);

  const card = el("div", { class: "fb-card" }, [
    el("div", { class: "fb-title" }, [isBug ? "Report a bug" : "Suggest a game"]),
    ta, errLine,
    el("div", { class: "fb-actions" }, [cancelBtn, sendBtn]),
  ]);
  const overlay = el("div", { class: "fb-overlay" }, [card]);

  const close = () => overlay.remove();
  cancelBtn.onclick = close;
  overlay.onclick = (e) => { if (e.target === overlay) close(); };

  sendBtn.onclick = async () => {
    const msg = ta.value.trim();
    if (!msg) { ta.focus(); return; }
    sendBtn.disabled = true; sendBtn.textContent = "Sending…"; errLine.hidden = true;
    const res = await submitFeedback(type, msg);
    if (res.ok) {
      card.replaceChildren(
        el("div", { class: "fb-title" }, ["Thanks!"]),
        el("div", { class: "fb-done" }, [isBug ? "Bug report sent. We read every one." : "Suggestion sent. We read every one."]),
        el("div", { class: "fb-actions" }, [(() => { const b = el("button", { class: "primary" }, ["Done"]); b.onclick = close; return b; })()]),
      );
    } else {
      sendBtn.disabled = false; sendBtn.textContent = "Send"; errLine.hidden = false;
    }
  };

  document.body.appendChild(overlay);
  ta.focus();
}

// ---------------- HOW TO PLAY (pre-match) ----------------

function showHowToPlay(meta: GameMeta, actionLabel: string, onConfirm: () => void) {
  const existing = document.querySelector(".htp-overlay");
  if (existing) existing.remove();

  const backBtn = backButton();
  const goBtn = el("button", { class: "primary htp-go" }, [actionLabel]);

  const overlay = el("div", { class: "htp-overlay" }, [
    el("div", { class: "htp-head" }, [backBtn, el("div", { class: "brand" }, ["How to play"]), el("span", {})]),
    el("div", { class: "htp-body" }, [
      (() => {
        const tile = el("div", { class: "htp-art" }, [svg(gameIcon(meta.id))]);
        tile.style.background = gameTint(meta.id);
        return tile;
      })(),
      el("div", { class: "htp-name" }, [meta.name]),
      el("div", { class: "card htp-card" }, [
        el("div", { class: "htp-sec" }, ["What it is"]),
        el("div", { class: "htp-p" }, [meta.blurb]),
      ]),
      el("div", { class: "card htp-card" }, [
        el("div", { class: "htp-sec" }, ["How to control it"]),
        el("div", { class: "htp-p" }, [HOW_TO_PLAY[meta.id] ?? "Controls appear on screen once the match starts."]),
      ]),
    ]),
    el("div", { class: "htp-foot" }, [goBtn]),
  ]);

  const close = () => overlay.remove();
  backBtn.onclick = close;
  goBtn.onclick = () => { close(); onConfirm(); };

  document.body.appendChild(overlay);
}

function logoTitle() {
  const wm = el("span", { class: "wordmark" }, ["JACE", el("span", { class: "wm-dot" }), "GAMES"]);
  wm.setAttribute("aria-label", "JACE.GAMES");
  return wm;
}

// ---------------- SPLASH (auto-join via QR / reconnect) ----------------

function renderSplash(title: string, sub: string) {
  app.replaceChildren(
    el("div", { class: "screen", id: "screen-splash" }, [
      el("h1", { class: "logo" }, [logoTitle()]),
      el("div", { class: "splash-spin" }),
      el("div", { class: "splash-title" }, [title]),
      el("div", { class: "hint centered" }, [sub]),
    ]),
  );
}

// ---------------- ROOM WIRING ----------------

function attach(r: Room) {
  room = r;
  mySlot = -1;
  homeError = "";
  reconnecting = false;
  deliberateLeave = false;
  lastToken = (r as any).reconnectionToken || lastToken;
  history.replaceState(null, "", "/play");
  wakeLock.enable();

  r.onStateChange(() => {
    lastToken = (r as any).reconnectionToken || lastToken;
    const me = r.state.players.get(r.sessionId);
    if (me) mySlot = me.slot;
    if (r.state.phase === "playing" || r.state.phase === "countdown") {
      if (currentView === "play" && game?.sync) game.sync();
    }
    render();
  });
  r.onMessage("matchEnd", (m: any) => {
    endSoundPlayed = false;
    playEndSound(m?.winnerSlot, m?.draw);
  });
  r.onMessage("soloResult", (m: any) => {
    soloResult = { game: String(m?.game || ""), score: Number(m?.score) || 0, detail: String(m?.detail || "") };
    if (currentView === "results") renderResults();
  });
  r.onMessage("board", (m: any) => {
    board = { game: String(m?.game || ""), top: (m?.top as BoardEntry[]) || [] };
    boardBusy = false;
    boardOpen = true;
    if (currentView === "results") renderResults();
    else if (currentView === "lobby") renderLobby();
  });
  r.onMessage("boardPosted", (m: any) => {
    board = {
      game: String(m?.game || ""),
      top: (m?.top as BoardEntry[]) || [],
      rank: Number(m?.rank) || undefined,
      best: !!m?.best,
    };
    boardBusy = false;
    boardOpen = true;
    sound.cheer();
    if (currentView === "results") renderResults();
  });
  r.onMessage("toLobby", () => {/* state change handles the view */});
  r.onLeave((code: number) => {
    // 4000 = consented leave; anything else (network drop, screen off) -> try to recover
    if (deliberateLeave || code === 4000 || !lastToken) {
      goHome("");
      return;
    }
    void tryReconnect();
  });
  r.onError((_c, msg) => { homeError = msg || "Connection error."; });
}

function sleepOrVisible(ms: number): Promise<void> {
  return new Promise((res) => {
    const done = () => { clearTimeout(t); document.removeEventListener("visibilitychange", onVis); res(); };
    const onVis = () => { if (document.visibilityState === "visible") done(); };
    const t = setTimeout(done, ms);
    document.addEventListener("visibilitychange", onVis);
  });
}

function reconnectPill(show: boolean) {
  let el0 = document.getElementById("reconnect-pill");
  if (show && !el0) {
    el0 = document.createElement("div");
    el0.id = "reconnect-pill";
    el0.className = "reconnect-pill";
    el0.textContent = "⟳ reconnecting…";
    document.body.appendChild(el0);
  } else if (!show && el0) {
    el0.remove();
  }
}

async function tryReconnect() {
  if (reconnecting) return;
  reconnecting = true;
  // keep the frozen game on screen with just a subtle pill for the first few
  // seconds — most backgrounding blips recover instantly on return
  reconnectPill(true);

  const started = Date.now();
  const LIMIT_MS = 42000; // match the server's ~45s seat hold
  let escalated = false;
  let attempt = 0;

  while (reconnecting && Date.now() - started < LIMIT_MS) {
    attempt++;
    if (!escalated && Date.now() - started > 5000) {
      escalated = true;
      reconnectPill(false);
      teardownPlay();
      currentView = "";
    }
    if (escalated) renderSplash("Reconnecting…", "hang tight");

    try {
      const r = await reconnectRoom(lastToken);
      reconnectPill(false);
      teardownPlay();          // drop the frozen client so the fresh room remounts
      currentView = "";
      attach(r);
      render();
      return;
    } catch {
      await sleepOrVisible(attempt < 4 ? 600 : 1500);
    }
  }
  reconnectPill(false);
  goHome("Lost the connection — couldn't get back to the room.");
}

function goHome(err: string) {
  reconnectPill(false);
  teardownPlay();
  wakeLock.disable();
  room = null;
  reconnecting = false;
  homeError = err;
  render();
}

function playEndSound(winnerSlot: number, draw: boolean) {
  if (endSoundPlayed) return;
  endSoundPlayed = true;
  sound.unlock();
  const st: any = room?.state;
  const soloRun = st && (
    st.gameId === "bubble" ||
    (st.gameId === "asteroids" && st.asteroids?.solo) ||
    (st.gameId === "invaders" && st.invaders?.solo) ||
    (st.gameId === "tetris" && st.tetris?.solo) ||
    (st.gameId === "snake" && st.snake?.solo) ||
    st.gameId === "hormuz" ||
    (st.gameId === "wordduel" && st.wordduel?.solo)
  );
  if (soloRun) sound.arcadeDeath();
  else if (draw) sound.whistle();
  else if (winnerSlot === mySlot) sound.cheer();
  else sound.groan();
}

function render() {
  if (reconnecting) return;
  if (!room) return renderHome();
  const phase = room.state.phase as string;
  const view = phase === "countdown" || phase === "playing" ? "play" : phase;
  if (view === "play") return ensurePlay();
  teardownPlay();
  if (view === "results") return renderResults();
  return renderLobby();
}

// ---------------- LOBBY ----------------

function renderLobby() {
  currentView = "lobby";
  const r = room!;
  const code = r.state.code as string;
  const [p0, p1] = playersBySlot();
  const me = r.state.players.get(r.sessionId) as any;
  const opponentHere = playersBySlot().filter(Boolean).length === 2;
  const botLevel = r.state.botLevel as string;
  const mode = (r.state.mode as PlayMode) || "1v1";
  const twoHumans = !!(p0 && p1 && !p0.isBot && !p1.isBot);
  const soloMode = mode === "solo";
  const cpuMode = mode === "cpu";

  const persistent = !!r.state.persistent;
  const modeBar = el("div", { class: "mode-bar" },
    // party rooms (Imposter) are native-app only — the web client has no renderer for them
    PLAY_MODES.filter((pm) => pm.id !== "party").map((pm) => {
      const locked = (twoHumans || persistent) && pm.id !== "1v1";
      const b = el("button", {
        class: `mode-opt ${mode === pm.id ? "on" : ""}`,
        disabled: locked,
        title: persistent && pm.id !== "1v1" ? "turn off “game never expires” first" : locked ? "another player is in this room" : pm.verb,
      }, [pm.name]);
      b.onclick = () => { if (mode !== pm.id) r.send("setMode", { mode: pm.id }); };
      return b;
    }),
  );

  const persistentToggle = el("button", {
    class: `toggle ${persistent ? "on" : ""}`,
    role: "switch",
  }, [el("span", { class: "knob" }, [])]);
  persistentToggle.setAttribute("aria-checked", String(persistent));
  persistentToggle.onclick = () => r.send("setPersistent", { on: !persistent });

  const diffPills = el("div", { class: "diff-pills" },
    BOT_LEVELS.map((lv) => {
      const b = el("button", { class: `diff ${botLevel === lv.id ? "on" : ""}` }, [
        el("span", { class: "dn" }, [lv.name]),
        el("span", { class: "db" }, [lv.blurb]),
      ]);
      b.onclick = () => r.send("botLevel", { level: lv.id });
      return b;
    }),
  );

  const shareBtn = el("button", { class: "ghost" }, ["Share invite"]);
  shareBtn.onclick = async () => {
    const url = `${location.origin}/play?code=${code}`;
    try {
      if (navigator.share) await navigator.share({ title: APP_NAME, text: `Play Jace.Games with me — code ${code}`, url });
      else { await navigator.clipboard.writeText(url); shareBtn.textContent = "Link copied!"; }
    } catch { /* dismissed */ }
  };
  const qrBtn = el("button", { class: "ghost" }, [qrOpen ? "Hide QR" : "Show QR"]);
  qrBtn.onclick = () => { qrOpen = !qrOpen; renderLobby(); };

  const qrPanel = qrOpen
    ? el("div", { class: "qr-panel" }, [
        svg(qrSvg(`${location.origin}/j/${code}`)),
        el("div", { class: "hint" }, ["scan to jump straight in"]),
      ])
    : null;

  const nameInput = el("input", { type: "text", value: me?.name ?? "", maxLength: NAME_MAX });
  nameInput.onchange = () => {
    rememberName(nameInput.value.trim());
    r.send("setName", { name: nameInput.value.trim() });
  };

  const playerCard = (p: any, slot: number) =>
    el("div", { class: `player ${p ? "" : "empty"} ${p?.ready ? "ready" : ""} ${p?.isBot ? "bot" : ""}` }, [
      el("div", { class: "slot" }, [
        p?.isBot ? "CPU" : `P${slot + 1}${slot === mySlot ? " (you)" : ""}`,
      ]),
      el("div", { class: "pname" }, [
        p ? (p.isBot ? cap(botLevel) : p.connected === false ? `${p.name} …` : p.name) : "waiting…",
      ]),
      el("div", { class: "status" }, [
        p ? (p.isBot ? "READY" : p.connected === false ? "reconnecting" : p.ready ? "READY" : "not ready") : "",
      ]),
    ]);

  const tabs = el("div", { class: "cat-tabs" },
    CATEGORIES.map((c) => {
      const b = el("button", { class: `cat ${lobbyCat === c.id ? "on" : ""}` }, [c.name]);
      b.onclick = () => { lobbyCat = c.id as any; renderLobby(); };
      return b;
    }),
  );

  const layoutToggle = el("div", { class: "layout-toggle" }, [
    layoutBtn("grid", "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z"),
    layoutBtn("list", "M4 6h16M4 12h16M4 18h16"),
  ]);
  function layoutBtn(mode: "grid" | "list", d: string) {
    const b = el("button", { class: `lt ${gameLayout === mode ? "on" : ""}`, title: `${mode} view` });
    b.setAttribute("aria-label", `${mode} view`);
    b.appendChild(svg(
      `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="${d}"/></svg>`,
    ));
    b.onclick = () => {
      gameLayout = mode;
      localStorage.setItem("su_gamelayout", mode);
      renderLobby();
    };
    return b;
  }

  const searchInput = el("input", {
    type: "text", class: "game-search", placeholder: "search games…", value: lobbySearch,
  });
  const listWrap = el("div");
  const makeCard = (g: GameMeta) => {
    const b = el("button", { class: `game ${r.state.gameId === g.id ? "selected" : ""}` }, [
      el("div", { class: "gart" }, [svg(gameIcon(g.id))]),
      el("div", { class: "grow" }, [
        el("div", { class: "gname" }, [g.name]),
        el("div", { class: "gblurb" }, [g.blurb]),
      ]),
      el("span", { class: "gtag" }, [g.category]),
    ]);
    (b.querySelector(".gart") as HTMLElement).style.background = gameTint(g.id);
    b.onclick = () => {
      r.send("pickGame", { gameId: g.id });
      if (g.id === "bubble") r.send("bubbleResume", loadBubble());
    };
    return b;
  };
  const gameGrid = (games: GameMeta[]) => {
    const grid = el("div", { class: `games ${gameLayout}` });
    grid.replaceChildren(...games.map(makeCard));
    return grid;
  };
  const refreshGameList = () => {
    const q = lobbySearch.trim().toLowerCase();
    const items = GAMES.filter((g) =>
      WEB_GAME_IDS.has(g.id) && // freeze: native-only games have no web client — see games/index.ts
      (!persistent || g.id === "worms") && // "game never expires" v1: Worms only — see ArenaRoom's PERSISTENT_GAME_IDS
      g.modes.includes(mode) &&
      (lobbyCat === "all" || g.category === lobbyCat) &&
      (!q || g.name.toLowerCase().includes(q) || g.blurb.toLowerCase().includes(q)),
    );
    if (!items.length) {
      listWrap.replaceChildren(el("div", { class: "empty-note" }, ["no games match"]));
      return;
    }
    const featured = (lobbyCat === "all" && !q)
      ? items.filter((g) => featuredIds.includes(g.id))
      : [];
    if (featured.length) {
      const rest = items.filter((g) => !featuredIds.includes(g.id));
      listWrap.replaceChildren(
        el("div", { class: "games-group-label" }, ["Featured"]),
        gameGrid(featured),
        el("div", { class: "games-group-label" }, ["All games"]),
        gameGrid(rest),
      );
    } else {
      listWrap.replaceChildren(gameGrid(items));
    }
  };
  searchInput.oninput = () => {
    lobbySearch = searchInput.value;
    refreshGameList();
    searchInput.focus();
  };
  refreshGameList();

  const selName = r.state.gameId ? gameMeta(r.state.gameId)?.name : "";
  // a game picked by a native-app friend before this client joined a shared
  // code — the web app has no renderer for it (freeze: native-only games
  // stay native, see games/index.ts). Block Ready rather than reaching a
  // blank play screen; the grid above already keeps this from happening via
  // the normal pick-a-game flow.
  const webUnsupported = !!r.state.gameId && !WEB_GAME_IDS.has(r.state.gameId);
  const iAmReady = !!me?.ready;
  // "never expires": take your first move solo — your friend joins straight
  // into the live match whenever they get to the invite, no need to wait
  // for them before you can start
  const canStart = !!r.state.gameId && !webUnsupported && (soloMode || cpuMode || opponentHere || persistent);
  const readyLabel = soloMode ? "Play" : iAmReady ? "Cancel ready" : "Ready!";
  const readyBtn = el("button", {
    class: iAmReady && !soloMode ? "" : "primary",
    disabled: !canStart,
  }, [readyLabel]);
  readyBtn.onclick = () => {
    if (!soloMode && iAmReady) { r.send("unready"); return; }
    const meta = gameMeta(r.state.gameId);
    if (meta) showHowToPlay(meta, readyLabel, () => r.send("ready"));
    else r.send("ready");
  };

  const hint = !r.state.gameId ? "Pick a game"
    : webUnsupported ? `${selName} isn't in the web app yet — open it in the JACE.GAMES app`
    : soloMode ? `Hit Play to start ${selName}`
    : cpuMode ? `Ready up to take on the computer at ${selName}`
    : persistent && !opponentHere ? "Take your first move — they'll pick it up whenever they're free"
    : !opponentHere ? "Waiting for your friend to join…"
    : `Ready up to start ${selName}`;

  const luckGame = !!gameMeta(r.state.gameId)?.luckOnly;
  const topCard = soloMode
    ? el("div", { class: "card solo-card" }, [
        el("div", { class: "hint centered" }, ["Just you. Pick a game and play — beat your own best."]),
      ])
    : cpuMode
    ? el("div", { class: "card cpu-card" }, luckGame
        ? [el("div", { class: "hint centered" }, ["Pure luck — just you and the CPU rolling. No difficulty to set."])]
        : [el("label", {}, ["Difficulty"]), diffPills])
    : el("div", { class: "card codebox" }, [
        el("div", { class: "big" }, [code]),
        el("div", { class: "hint" }, [persistent ? "one code — always leads back to this match" : "one code — works for every game"]),
        el("div", { class: "row" }, [shareBtn, qrBtn]),
        ...(qrPanel ? [qrPanel] : []),
        el("div", { class: "settings-divider" }, []),
        el("div", { class: "settings-row" }, [
          el("div", { class: "settings-row-text" }, [
            el("div", { class: "srl" }, ["Game never expires"]),
            el("div", { class: "srs" }, [
              persistent
                ? "Artillery Worms only, no turn timer — pick up this match anytime, even weeks from now."
                : "Async play with a friend — no turn timer, resume the match anytime. Artillery Worms only.",
            ]),
          ]),
          persistentToggle,
        ]),
      ]);

  const players = soloMode
    ? []
    : [el("div", { class: "players" }, [playerCard(p0, 0), playerCard(p1, 1)])];

  const showLbPeek = !!r.state.gameId && LEADERBOARD_IDS.has(r.state.gameId) &&
    (soloMode || mode === "1v1");
  const lbPeek = showLbPeek ? [lobbyLeaderboard(r)] : [];

  app.replaceChildren(
    el("div", { class: "screen", id: "screen-lobby" }, [
      el("div", { class: "brand" }, ["How you're playing"]),
      modeBar,
      topCard,
      ...players,
      el("div", { class: "card" }, [
        el("label", {}, ["Your name — shown on leaderboards"]), nameInput,
      ]),
      el("div", { class: "card games-card" }, [
        el("div", { class: "games-head" }, [el("label", {}, ["Choose a game"]), layoutToggle]),
        tabs,
        searchInput,
        listWrap,
      ]),
      ...lbPeek,
      adSlot(),
      el("div", { class: "lobby-dock" }, [
        el("div", { class: "hint centered" }, [hint]),
        el("div", { class: "row" }, [readyBtn]),
      ]),
    ]),
  );
}

// ---------------- PLAY ----------------

function ensurePlay() {
  if (currentView === "play") return;
  currentView = "play";
  endSoundPlayed = false;
  // clear the previous match's leaderboard state as a fresh run begins
  soloResult = null;
  board = null;
  boardBusy = false;
  boardOpen = false;
  lbLobbyOpen = false;
  const playRoot = el("div", { class: "screen", id: "screen-play" });
  const quit = el("button", { class: "quit-btn", title: "Quit to lobby" }, [
    svg(`<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"><path d="M2.5 2.5 L11.5 11.5 M11.5 2.5 L2.5 11.5"/></svg>`),
  ]);
  quit.setAttribute("aria-label", "Quit to lobby");
  quit.onclick = () => { if (confirm("Quit this game?")) room?.send("leaveMatch"); };
  playRoot.appendChild(quit);

  // "beat this" banner for a solo run of a leaderboard game
  const gid = room!.state.gameId as string;
  if (room!.state.mode === "solo" && LEADERBOARD_IDS.has(gid)) {
    const banner = el("div", { class: "best-banner" }, ["Best · …"]);
    playRoot.appendChild(banner);
    fetchBest(gid).then((b) => {
      if (b && b.score > 0) banner.textContent = `Best ${fmtScore(b.score)} · ${b.name}`;
      else banner.remove();
    }).catch(() => banner.remove());
  }

  app.replaceChildren(playRoot);
  game = makeGameClient(room!.state.gameId);
  game?.mount({ root: playRoot, room: room!, mySlot });
}

function teardownPlay() {
  if (game) { game.unmount(); game = null; }
  if (currentView === "play") currentView = "";
}

// ---------------- RESULTS ----------------

function leaderboardCard(r: Room): HTMLElement {
  const gid = r.state.gameId as string;
  const unit = gameMeta(gid)?.unit || "score";
  const haveBoard = !!board && board.game === gid && boardOpen;

  const card = el("div", { class: "card lb-card" }, [el("label", {}, [`${gameMeta(gid)?.name ?? "Solo"} leaderboard`])]);

  if (haveBoard) {
    if (board!.rank) {
      card.appendChild(el("div", { class: "lb-you" }, [
        board!.best ? `New personal best — #${board!.rank}` : `Your best still stands — #${board!.rank}`,
      ]));
    }
    card.appendChild(renderBoardList(board!.top));
    if (soloResult && !board!.rank && !boardBusy) card.appendChild(postRow(r));
    return card;
  }

  if (soloResult) {
    card.appendChild(el("div", { class: "lb-score" }, [
      el("span", { class: "n" }, [fmtScore(soloResult.score)]),
      el("span", { class: "u" }, [soloResult.detail || unit]),
    ]));
    card.appendChild(postRow(r));
  }
  const viewBtn = el("button", { class: "ghost small" }, [
    boardBusy ? "Loading…" : soloResult ? "Just show the top scores" : "View the leaderboard",
  ]);
  (viewBtn as HTMLButtonElement).disabled = boardBusy;
  viewBtn.onclick = () => { boardBusy = true; r.send("getBoard", { game: gid, pid: playerId() }); renderResults(); };
  card.appendChild(viewBtn);
  return card;
}

function postRow(r: Room): HTMLElement {
  const nameInput = el("input", {
    type: "text", value: savedName(), maxLength: NAME_MAX, placeholder: "your name",
  });
  const btn = el("button", { class: "primary" }, [boardBusy ? "Posting…" : "Post my score"]);
  const refresh = () => { (btn as HTMLButtonElement).disabled = boardBusy || !nameInput.value.trim(); };
  refresh();
  nameInput.oninput = refresh;
  btn.onclick = () => {
    const n = nameInput.value.trim().slice(0, NAME_MAX);
    if (!n) return;
    rememberName(n);
    boardBusy = true;
    r.send("postScore", { name: n, pid: playerId() });
    renderResults();
  };
  return el("div", { class: "lb-post" }, [nameInput, btn]);
}

function renderBoardList(top: BoardEntry[]): HTMLElement {
  if (!top.length) return el("div", { class: "hint centered" }, ["No scores yet — be the first."]);
  const seen = new Map<string, number>();
  top.forEach((e) => seen.set(e.name, (seen.get(e.name) || 0) + 1));
  return el("div", { class: "lb-list" },
    top.map((e, i) => el("div", { class: `lb-row${e.mine ? " mine" : ""}` }, [
      el("span", { class: "lb-rank" }, [`${i + 1}`]),
      el("div", { class: "lb-mid" }, [
        el("span", { class: "lb-name" }, [
          (e.name || "?") + ((seen.get(e.name) || 0) > 1 ? ` #${e.tag}` : ""),
        ]),
        ...(e.detail ? [el("span", { class: "lb-detail" }, [e.detail])] : []),
      ]),
      el("span", { class: "lb-sc" }, [fmtScore(e.score)]),
    ])),
  );
}

function lobbyLeaderboard(r: Room): HTMLElement {
  const gid = r.state.gameId as string;
  const fresh = !!board && board.game === gid;
  if (lbLobbyOpen && !fresh && !boardBusy) {
    boardBusy = true;
    r.send("getBoard", { game: gid, pid: playerId() });
  }
  const toggle = el("button", { class: "ghost small" }, [lbLobbyOpen ? "Hide" : "Show"]);
  toggle.onclick = () => { lbLobbyOpen = !lbLobbyOpen; renderLobby(); };
  const card = el("div", { class: "card lb-card" }, [
    el("div", { class: "lb-head" }, [el("label", {}, ["Top scores"]), toggle]),
  ]);
  if (lbLobbyOpen) {
    card.appendChild(fresh
      ? renderBoardList(board!.top.slice(0, 10))
      : el("div", { class: "hint centered" }, ["Loading…"]));
  }
  return card;
}

function renderResults() {
  currentView = "results";
  const r = room!;
  const [p0, p1] = playersBySlot();
  const draw = r.state.draw as boolean;
  const winSlot = r.state.winnerSlot as number;
  const meWon = winSlot === mySlot;
  playEndSound(winSlot, draw);

  const soloRun =
    r.state.gameId === "bubble" ||
    (r.state.gameId === "asteroids" && r.state.asteroids?.solo) ||
    (r.state.gameId === "invaders" && r.state.invaders?.solo) ||
    (r.state.gameId === "tetris" && r.state.tetris?.solo) ||
    (r.state.gameId === "snake" && r.state.snake?.solo) ||
    r.state.gameId === "hormuz" ||
    (r.state.gameId === "wordduel" && r.state.wordduel?.solo) ||
    (r.state.gameId === "tug" && r.state.tug?.solo) ||
    (r.state.gameId === "wordbattle" && r.state.wordbattle?.solo);

  // full-screen coloured ground + one huge line, like the native app's results:
  // a solo run's score is its headline (from the game's detail block), a match
  // says who won — mango when you won, cobalt when they did, grape for a draw
  const cpu = r.state.mode === "cpu";
  const rival = winSlot === 0 ? p0 : winSlot === 1 ? p1 : null;
  const headline = soloRun ? ""
    : draw ? "It's a draw!"
    : cpu ? (meWon ? "You beat Dot!" : "Dot wins this one")
    : meWon ? "You win!"
    : `${(rival?.isBot ? "Dot" : rival?.name) || `Player ${winSlot + 1}`} wins!`;
  const ground = soloRun ? "var(--mint)" : draw ? "var(--grape)" : meWon ? "var(--mango)" : "var(--cobalt)";
  const line = headline ? el("div", { class: "results-line" }, [headline]) : null;

  let detail: HTMLElement | null = null;
  try {
    const gc = makeGameClient(r.state.gameId);
    if (gc?.results) detail = gc.results({ root: document.createElement("div"), room: r, mySlot });
  } catch (e) {
    console.error("results detail failed", e);
  }

  const me = r.state.players.get(r.sessionId) as any;
  const canPost = soloRun && LEADERBOARD_IDS.has(r.state.gameId);
  const lbCard = canPost ? leaderboardCard(r) : null;

  const rematchBtn = el("button", { class: "primary" }, [
    me?.ready ? "Waiting for opponent…" : soloRun ? "Play again" : "Rematch",
  ]);
  rematchBtn.disabled = !!me?.ready;
  rematchBtn.onclick = () => r.send("rematch");

  const lobbyBtn = el("button", { class: "text-link" }, ["Back to games"]);
  lobbyBtn.onclick = () => r.send("toLobby");

  const screen = el("div", { class: `screen${soloRun ? " solo" : ""}`, id: "screen-results" }, [
    el("div", { class: "res-eyebrow" }, [gameMeta(r.state.gameId)?.name ?? "Final"]),
    ...(line ? [line] : []),
    ...(detail ? [detail] : soloRun ? [el("div", { class: "results-line" }, ["Game over"])] : []),
    ...(lbCard ? [lbCard] : []),
    adSlot(),
    el("div", { class: "lobby-dock" }, [
      el("div", { class: "row" }, [rematchBtn]),
      lobbyBtn,
    ]),
  ]);
  screen.style.setProperty("--res-bg", ground);
  app.replaceChildren(screen);
}

// ---------------- BOOT ----------------

const joinPath = location.pathname.match(/^\/j\/([A-Za-z0-9]{4})\/?$/);
if (joinPath) {
  const code = joinPath[1]!.toUpperCase();
  renderSplash("Joining…", `room ${code}`);
  joinRoom(code, savedName() || "Player")
    .then(attach)
    .then(render)
    .catch((e: any) => { homeError = e?.message || "That room isn't open."; goHome(homeError); });
} else {
  render();
}
